import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, House, Plus, X } from 'lucide-react';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api, type Paginated } from '../lib/api';
import { P, useAuth } from '../lib/auth';
import { useI18n, type Locale, type TranslationKey } from '../lib/i18n';
import { formatDate, formatNumber, relativeTime } from '../lib/format';
import {
  REMOTE_WORK_STATUSES,
  remoteWorkTone,
  useWaitingRemoteWork,
  type RemoteWork,
} from '../lib/remote-work';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorState,
  Input,
  LoadingBlock,
  PageHeader,
  Pagination,
  Select,
  Table,
  Tabs,
  Td,
  Th,
  Tr,
  useToast,
} from '../components/ui';

type Scope = 'mine' | 'review' | 'all';

/** "31 Oct 2026" or "31 Oct 2026 – 2 Nov 2026". */
export function remoteWorkDates(request: { startDate: string; endDate: string }, locale: Locale): string {
  const start = formatDate(request.startDate, locale);
  return request.endDate === request.startDate ? start : `${start} – ${formatDate(request.endDate, locale)}`;
}

/**
 * Route: `/remote-work` — my requests to work remotely; and for a team leader or
 * an administrator, what waits for their approval and everything they may see
 * (operator, 2026-10-28: "need team leader approval first then admin approval …
 * one by one"). Only those who work from the office ask. A row opens its request.
 */
export function RemoteWorkPage() {
  const { t } = useI18n();
  const { user, can, ledDepartmentIds } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const waiting = useWaitingRemoteWork();
  // A team leader gives the first approval to their departments' people; an administrator the second, to anyone.
  const reviewer = can(P.REMOTE_WORK_APPROVE) || ledDepartmentIds.length > 0;
  // Those who work remotely already have nothing to ask for. An API without the field: at the office.
  const atOffice = (user?.workMode ?? 'OFFICE') === 'OFFICE';
  const requested = params.get('tab') as Scope | null;
  const tab: Scope = !reviewer ? 'mine' : requested === 'mine' || requested === 'all' ? requested : 'review';

  return (
    <>
      <PageHeader
        title={t('remoteWork.title')}
        subtitle={t('remoteWork.subtitle')}
        action={
          atOffice && (
            <Button variant="primary" icon={<Plus className="h-4 w-4" />} onClick={() => navigate('/remote-work/new')}>
              {t('remoteWork.new')}
            </Button>
          )
        }
      />
      {!atOffice && (
        <div className="mb-5 rounded-md border hairline bg-neutral-500/8 px-4 py-2.5 text-body-sm text-muted">
          {t('remoteWork.atRemote')}
        </div>
      )}
      {reviewer && (
        <div className="mb-5">
          <Tabs
            active={tab}
            onChange={(id) => setParams({ tab: id }, { replace: true })}
            tabs={[
              { id: 'review', label: t('remoteWork.review'), count: waiting.review || undefined },
              { id: 'all', label: t('remoteWork.all') },
              { id: 'mine', label: t('remoteWork.mine') },
            ]}
          />
        </div>
      )}
      <RemoteWorkList key={tab} scope={tab} />
    </>
  );
}

function RemoteWorkList({ scope }: { scope: Scope }) {
  const { t, locale, localized } = useI18n();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['remote-work', 'list', scope, page, status, q],
    queryFn: () =>
      api.get<Paginated<RemoteWork>>('/remote-work', { scope, page, pageSize: 25, status: status || undefined, q: q || undefined }),
  });

  const act = useMutation({
    mutationFn: ({ id, action }: { id: string; action: 'approve' | 'cancel' }) =>
      api.post<RemoteWork>(`/remote-work/${id}/${action}`, {}),
    onSuccess: (result, { action }) => {
      toast.success(
        action === 'cancel'
          ? t('remoteWork.cancelledToast')
          : result.status === 'APPROVED'
            ? t('remoteWork.approvedToast')
            : t('remoteWork.approvedLeaderToast'),
      );
      void queryClient.invalidateQueries({ queryKey: ['remote-work'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const showEmployee = scope !== 'mine';

  return (
    <>
      {scope !== 'mine' && (
        <Card className="mb-4" padded={false}>
          <div className="grid gap-3 p-4 sm:grid-cols-3">
            <Input placeholder={t('common.search')} value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} />
            {scope === 'all' && (
              <Select value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }} aria-label={t('common.status')}>
                <option value="">{t('common.status')} — {t('common.all')}</option>
                {REMOTE_WORK_STATUSES.map((s) => (
                  <option key={s} value={s}>{t(`remoteWork.status.${s}` as TranslationKey)}</option>
                ))}
              </Select>
            )}
          </div>
        </Card>
      )}

      <Card padded={false}>
        {isLoading ? (
          <LoadingBlock />
        ) : error ? (
          <ErrorState error={error} onRetry={() => void refetch()} />
        ) : !data?.items.length ? (
          <EmptyState
            icon={<House className="h-8 w-8" />}
            title={scope === 'review' ? t('remoteWork.nothingToReview') : t('remoteWork.none')}
            hint={scope === 'mine' ? t('remoteWork.noneHint') : ''}
          />
        ) : (
          <>
            <Table>
              <thead>
                <tr>
                  {showEmployee && <Th>{t('common.employee')}</Th>}
                  <Th>{t('remoteWork.dates')}</Th>
                  <Th align="end">{t('remoteWork.days')}</Th>
                  <Th>{t('common.status')}</Th>
                  <Th>{t('common.reason')}</Th>
                  <Th align="end">{t('common.actions')}</Th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((r) => (
                  <Tr key={r.id} onClick={() => navigate(`/remote-work/${r.id}`)}>
                    {showEmployee && (
                      <Td>
                        <span className="block">{localized(r.user)}</span>
                        {r.department && <span className="text-caption text-faint">{localized(r.department)}</span>}
                      </Td>
                    )}
                    <Td className="tabular whitespace-nowrap text-muted">{remoteWorkDates(r, locale)}</Td>
                    <Td align="end" className="tabular">{formatNumber(r.days, locale)}</Td>
                    <Td>
                      <Badge tone={remoteWorkTone(r.status)}>{t(`remoteWork.status.${r.status}` as TranslationKey)}</Badge>
                      <StepsLine request={r} />
                    </Td>
                    <Td className="max-w-xs">
                      <span className="block truncate text-muted" title={r.reason}>{r.reason}</span>
                      {(r.adminNote || r.leaderNote) && (
                        <span className="block truncate text-caption text-faint" title={(r.adminNote || r.leaderNote) ?? undefined}>
                          “{r.adminNote || r.leaderNote}”
                        </span>
                      )}
                      {scope === 'review' && <span className="block text-caption text-faint">{relativeTime(r.createdAt, locale)}</span>}
                    </Td>
                    <Td align="end">
                      <div className="flex justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                        {r.can.decide && (
                          <>
                            <Button
                              size="sm"
                              variant="ghost"
                              icon={<Check className="h-3.5 w-3.5" />}
                              disabled={act.isPending}
                              onClick={() => act.mutate({ id: r.id, action: 'approve' })}
                            >
                              {t('common.approve')}
                            </Button>
                            <Button size="sm" variant="ghost" icon={<X className="h-3.5 w-3.5" />} onClick={() => navigate(`/remote-work/${r.id}/reject`)}>
                              {t('common.reject')}
                            </Button>
                          </>
                        )}
                        {r.can.cancel && (
                          <Button size="sm" variant="ghost" disabled={act.isPending} onClick={() => act.mutate({ id: r.id, action: 'cancel' })}>
                            {t('remoteWork.cancel')}
                          </Button>
                        )}
                      </div>
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
            <Pagination page={data.page} pageCount={data.pageCount} total={data.total} onChange={setPage} />
          </>
        )}
      </Card>
    </>
  );
}

/**
 * The two approvals under the status, one a line: who gave the team leader's and
 * the administrator's so far. Nothing given yet: nothing to say.
 */
function StepsLine({ request }: { request: RemoteWork }) {
  const { t, localized } = useI18n();
  const given = [
    request.leaderDecidedAt && { step: 'LEADER', who: request.leader },
    request.adminDecidedAt && { step: 'ADMIN', who: request.admin },
  ].filter((given): given is { step: 'LEADER' | 'ADMIN'; who: RemoteWork['leader'] } => !!given);
  if (given.length === 0) return null;
  return (
    <span className="mt-0.5 block text-caption text-faint">
      {given.map(({ step, who }) => (
        <span key={step} className="block">
          {t(`remoteWork.step.${step}`)}: {who ? localized(who) : '—'}
        </span>
      ))}
    </span>
  );
}
